import styled from "@emotion/styled";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { JsonLd } from "react-schemaorg";
import type { VideoObject } from "schema-dts";

import {
  durationToISOString,
  getAbsoluteUrl,
  getYoutubeThumbnailUrl,
  parseDate,
} from "shared/lib/utils";

import type { ContentSchemaRenderProps } from "scmp-app/components/schema-render/content";
import { useRenderContext } from "scmp-app/components/schema-render/content";
import type { Props as SCMPYouTubeVideoPlayerProps } from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-player";
import type { scmpYoutubeContent$key } from "scmp-app/queries/__generated__/scmpYoutubeContent.graphql";

import { StyledSCMPYoutubeVideo } from "./styles";

export type ExtraProps = {
  enableAutoPlay?: boolean;
  gaData?: SCMPYouTubeVideoPlayerProps["gaData"];
  isEnabledImaManager?: boolean;
  isMuteVideo?: boolean;
  skipAdInfo?: boolean;
};

export type Props = ContentSchemaRenderProps &
  ExtraProps & {
    "data-duration"?: string;
    "data-hero-video"?: string;
    "data-nid"?: string;
    "data-poster"?: string;
    "data-poster-lg"?: string;
    "data-poster-sm"?: string;
    "data-youtube-id"?: string;
    reference: scmpYoutubeContent$key;
    src?: string;
    title?: string;
  };

const Component: FunctionComponent<Props> = ({
  enableAutoPlay = true,
  gaData,
  index,
  isEnabledImaManager = true,
  reference: reference_,
  skipAdInfo,
  src,
  title,
  ...attribs
}: Props) => {
  const content = useFragment(
    graphql`
      fragment scmpYoutubeContent on Content {
        entityId
        urlAlias
        publishedDate
      }
    `,
    reference_,
  );

  const { advertising } = useRenderContext();

  const duration = attribs["data-duration"];
  const youtubeVideoId = attribs["data-youtube-id"];
  const entityId = attribs["data-nid"];

  const youtubeThumbnails = useMemo(
    () => (youtubeVideoId ? getYoutubeThumbnailUrl(youtubeVideoId) : []),
    [youtubeVideoId],
  );

  const imageSource = useMemo(() => first(youtubeThumbnails), [youtubeThumbnails]);

  const handleRenderVideoSchema = useCallback(() => {
    const [minutes, seconds] = duration?.split(":") ?? [];

    const uploadDate = content?.publishedDate
      ? parseDate(content?.publishedDate).toISOString()
      : null;
    // refs: https://developers.google.com/search/docs/appearance/structured-data/video#video-object
    if (!title || !uploadDate) return false;
    return (
      <JsonLd<VideoObject>
        item={{
          "@context": "https://schema.org",
          "@type": "VideoObject",
          contentUrl: src,
          description: title,
          duration: durationToISOString(
            Number.parseInt(minutes ?? "0"),
            Number.parseInt(seconds ?? "0"),
          ),
          embedUrl: `${getAbsoluteUrl(content?.urlAlias)}`,
          name: title,
          thumbnailUrl: youtubeThumbnails,
          uploadDate,
        }}
      />
    );
  }, [content?.publishedDate, content?.urlAlias, duration, src, title, youtubeThumbnails]);

  const adInfo = useMemo(
    () => ({
      targeting: {
        ...advertising?.targeting,
      },
      zone: `${advertising?.zone ?? ""}/${advertising?.instream ?? "instream1"}`,
    }),
    [advertising?.instream, advertising?.targeting, advertising?.zone],
  );

  return (
    <>
      {handleRenderVideoSchema()}
      <StyledSCMPYoutubeVideo
        adInfo={skipAdInfo ? undefined : adInfo}
        duration={duration}
        enableAutoPlay={enableAutoPlay}
        entityId={entityId}
        gaData={{
          ...gaData,
          ga4ArticleEntityId: content?.entityId,
        }}
        imageSource={imageSource}
        index={index}
        isEnabledImaManager={isEnabledImaManager}
        title={title}
        withFooter={{ variant: "default" }}
        youtubeVideoId={youtubeVideoId}
        {...attribs}
      />
    </>
  );
};

Component.displayName = "SCMPYoutube";
export const SCMPYoutube = styled(Component)``;
